import { Button } from "primereact/button";
import { ConfirmDialog, confirmDialog } from "primereact/confirmdialog";
import { Dialog } from "primereact/dialog";
import { Dropdown } from "primereact/dropdown";
import { InputText } from "primereact/inputtext";
import { Menu } from "primereact/menu";
import { Toast } from "primereact/toast";
import React, { useEffect, useRef, useState } from "react";
import Http from "../../../Common/Http";
import IsLogin from "../../../Common/IsLogin";
import CreateStory from "../Story/Create";
import EditStory from "../Story/Edit";
import CreateTask from "../Task/Create";
import EditTask from "../Task/Edit";
import StatusUpdate from "../Task/StatusUpdate";

// CSS for status dropdowns
const statusDropdownStyle = `
  .status-dropdown-container .p-dropdown {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100px;
    border: none;
    padding: 0;
  }
  
  .status-dropdown-container .p-dropdown-label {
    padding: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    color: white;
    font-weight: 500;
    line-height: 1;
    font-size: 12px;
    text-align: center;
  }
  
  .status-dropdown-container .p-dropdown-trigger {
    display: none;
  }
  
  .status-dropdown-container .p-dropdown-panel {
    margin-top: 5px;
    box-shadow: 0 3px 10px rgba(0,0,0,0.2);
    border-radius: 4px;
  }
  
  .status-dropdown-container .p-dropdown-panel .p-dropdown-items {
    padding: 4px;
  }
  
  .status-dropdown-container .p-dropdown-panel .p-dropdown-item {
    padding: 4px 8px;
    margin: 2px 0;
    border-radius: 3px;
    font-size: 12px;
    text-align: center;
  }
  
  .status-dropdown-container .p-dropdown:focus,
  .status-dropdown-container .p-dropdown:hover {
    box-shadow: 0 0 0 2px rgba(255,255,255,0.2);
    border: none;
    outline: none;
  }
  
  .status-dropdown-container .p-dropdown {
    transition: all 0.2s ease;
  }

  /* Column-based backlog layout */
  .backlog-container {
    overflow: auto;
  }

  .backlog-item {
    transition: background-color 0.2s ease;
  }

  .task-description {
    margin-left: 60px !important;
    padding-left: 0 !important;
  }
`;

const Backlog = () => {
  // References
  const toast = useRef(null);
  const createMenuRef = useRef(null);

  // State variables
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [searchTerm, setSearchTerm] = useState("");

  // Current user state
  const [currentUser, setCurrentUser] = useState(null);

  // Data states
  const [stories, setStories] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [expandedStories, setExpandedStories] = useState({});
  const [individualTasksExpanded, setIndividualTasksExpanded] = useState(true);
  const [allExpanded, setAllExpanded] = useState(true);

  // State to track highlighted items (newly created or edited)
  const [highlightedItems, setHighlightedItems] = useState({});

  // Dialog visibility states
  const [createStoryVisible, setCreateStoryVisible] = useState(false);
  const [editStoryVisible, setEditStoryVisible] = useState(false);
  const [createTaskVisible, setCreateTaskVisible] = useState(false);
  const [editTaskVisible, setEditTaskVisible] = useState(false);
  const [statusUpdateVisible, setStatusUpdateVisible] = useState(false);

  // Selection states
  const [selectedStory, setSelectedStory] = useState(null);
  const [selectedTask, setSelectedTask] = useState(null);

  // State for storing full story data for editing
  const [storyDataForEdit, setStoryDataForEdit] = useState(null);

  // State for storing full task data for editing
  const [taskDataForEdit, setTaskDataForEdit] = useState(null);

  // Mapping data
  const [projects, setProjects] = useState({});
  const [labels, setLabels] = useState({});
  const [developers, setDevelopers] = useState({});

  // Status options for task status dropdowns
  const statusOptions = [
    { label: "To Do", value: "To Do" },
    { label: "In Progress", value: "In Progress" },
    { label: "Pause", value: "Pause" },
    { label: "Review", value: "Review" },
    { label: "Done", value: "Done" },
  ];

  // Function to check if a status transition is valid
  const isValidStatusTransition = (currentStatus, newStatus) => {
    // Special handling for Pause status
    if (newStatus === "Pause") {
      // Can only pause if task is in In Progress or Review, not To Do
      return currentStatus === "In Progress" || currentStatus === "Review";
    }

    // Special handling for returning from Pause
    if (currentStatus === "Pause") {
      // Can resume to In Progress or Review
      return newStatus === "In Progress" || newStatus === "Review" || newStatus === "Done";
    }

    // For regular forward progression
    const statusOrder = ["To Do", "In Progress", "Review", "Done"];
    const currentIndex = statusOrder.indexOf(currentStatus);
    const newIndex = statusOrder.indexOf(newStatus);

    // Only allow moving forward in the workflow (skipping Pause in the regular flow)
    return newIndex > currentIndex;
  };

  // Function to check if current user is assigned to a task
  const isUserAssignedToTask = (task) => {
    if (!currentUser || !task || !task.developers) return false;

    // Check if the current user is among the assigned developers
    return task.developers.some((dev) => dev.id === currentUser.id);
  };

  // Create menu items
  const createMenuItems = [
    {
      label: "Story",
      icon: "pi pi-book",
      command: () => setCreateStoryVisible(true),
    },
    {
      label: "Task",
      icon: "pi pi-check-square",
      command: () => setCreateTaskVisible(true),
    },
  ];

  // Handle adding a new task without refreshing all data
  const handleAddNewTask = (newTaskData) => {
    if (!newTaskData) return;

    // Add the newly created task to the tasks state
    setTasks((prevTasks) => {
      // Make sure we're not adding a duplicate
      const exists = prevTasks.some((task) => task.id === newTaskData.id);
      if (exists) return prevTasks;

      // Add developer data if it's not included in the newTaskData
      const taskWithDevelopers = {
        ...newTaskData,
        developers: Array.isArray(newTaskData.developers) ? newTaskData.developers : newTaskData.developer_ids?.map((id) => ({ id })) || [],
      };

      // Highlight the newly created task
      highlightItem(taskWithDevelopers.id, "task");

      return [...prevTasks, taskWithDevelopers];
    });
  };

  // Handle updating a task without refreshing all data
  const handleUpdateTask = (updatedTaskData) => {
    if (!updatedTaskData) return;

    setTasks((prevTasks) => {
      return prevTasks.map((task) => {
        if (task.id === updatedTaskData.id) {
          // If we only received partial data (just id and status), merge it with existing task data
          const taskWithUpdates = {
            ...task, // Keep all existing task data
            ...updatedTaskData, // Override with any updated fields
            // Make sure developers are properly structured
            developers: Array.isArray(updatedTaskData.developers)
              ? updatedTaskData.developers
              : updatedTaskData.developer_ids?.map((id) => ({ id })) || task.developers || [],
          };

          // Highlight the updated task
          highlightItem(taskWithUpdates.id, "task");

          return taskWithUpdates;
        }
        return task;
      });
    });
  };

  // Handle adding a new story without refreshing all data
  const handleAddNewStory = (newStoryData) => {
    if (!newStoryData) return;

    // Add the newly created story to the stories state
    setStories((prevStories) => {
      // Make sure we're not adding a duplicate
      const exists = prevStories.some((story) => story.id === newStoryData.id);
      if (exists) return prevStories;

      // Initialize expanded state for the new story
      setExpandedStories((prev) => ({
        ...prev,
        [newStoryData.id]: true,
      }));

      // Highlight the newly created story
      highlightItem(newStoryData.id, "story");

      return [...prevStories, newStoryData];
    });
  };

  // Handle updating a story without refreshing all data
  const handleUpdateStory = (updatedStoryData) => {
    if (!updatedStoryData) return;

    setStories((prevStories) => {
      return prevStories.map((story) => {
        if (story.id === updatedStoryData.id) {
          // Highlight the updated story
          highlightItem(updatedStoryData.id, "story");

          return updatedStoryData;
        }
        return story;
      });
    });
  };

  // Function to highlight an item for 2 seconds
  const highlightItem = (itemId, itemType) => {
    // Set the highlight
    setHighlightedItems((prev) => ({
      ...prev,
      [`${itemType}-${itemId}`]: true,
    }));

    // Remove the highlight after 2 seconds
    setTimeout(() => {
      setHighlightedItems((prev) => {
        const newState = { ...prev };
        delete newState[`${itemType}-${itemId}`];
        return newState;
      });
    }, 2000);
  };

  // State for expanded task descriptions
  const [expandedDescriptions, setExpandedDescriptions] = useState({});

  // Toggle description expansion
  const toggleDescriptionExpansion = (taskId, e) => {
    e.stopPropagation();
    setExpandedDescriptions((prev) => ({
      ...prev,
      [taskId]: !prev[taskId],
    }));
  };

  // State for task types data
  const [taskTypes, setTaskTypes] = useState({});

  // Fetch task types
  const fetchTaskTypes = async () => {
    try {
      const res = await Http.get(`/task-type`);
      const taskTypesMap = {};
      if (res.data && res.data.data) {
        res.data.data.forEach((type) => {
          taskTypesMap[type.id] = {
            name: type.task_type,
            icon: type.icon || "pi pi-check-square",
            color: type.color || "#4CAF50",
          };
        });
      }
      setTaskTypes(taskTypesMap);
    } catch (error) {
      console.error("Error fetching task types:", error);
    }
  };

  // Toggle all sections expanded/collapsed state
  const toggleAllExpanded = () => {
    const newExpandedState = !allExpanded;
    setAllExpanded(newExpandedState);

    // Update stories expanded state
    const updatedExpandedStories = {};
    stories.forEach((story) => {
      updatedExpandedStories[story.id] = newExpandedState;
    });
    setExpandedStories(updatedExpandedStories);

    // Update individual tasks expanded state
    setIndividualTasksExpanded(newExpandedState);
  };

  // Fetch all necessary data
  useEffect(() => {
    fetchData();
    fetchCurrentUser();
  }, []);

  // Fetch current logged in user
  const fetchCurrentUser = async () => {
    try {
      const response = await Http.get("/me");
      if (response.data && response.data.data) {
        setCurrentUser(response.data.data);
        console.log("Current user data:", response.data.data);
      }
    } catch (error) {
      console.error("Error fetching current user:", error);
    }
  };

  const fetchData = async () => {
    setLoading(true);
    setError("");

    try {
      // Fetch stories and tasks in parallel
      const [storiesResponse, tasksResponse] = await Promise.all([Http.get("/story"), Http.get("/task")]);

      // Set stories and tasks
      setStories(storiesResponse.data.data || []);
      setTasks(tasksResponse.data.data || []);

      // Fetch related data for displaying names instead of IDs
      await Promise.allSettled([fetchProjects(), fetchLabels(), fetchDevelopers(), fetchTaskTypes()]);

      // Initialize expanded state for stories
      const initialExpandedState = {};
      storiesResponse.data.data?.forEach((story) => {
        initialExpandedState[story.id] = true;
      });
      setExpandedStories(initialExpandedState);
    } catch (error) {
      console.error("Error fetching backlog data:", error);
      setError("Failed to load backlog data. Please refresh the page to try again.");
    } finally {
      setLoading(false);
    }
  };

  // Fetch projects
  const fetchProjects = async () => {
    try {
      const res = await Http.get(`/project`);
      const projectsMap = {};
      if (res.data && res.data.data) {
        res.data.data.forEach((project) => {
          projectsMap[project.id] = project.title;
        });
      }
      setProjects(projectsMap);
    } catch (error) {
      console.error("Error fetching projects:", error);
    }
  };

  // Fetch labels
  const fetchLabels = async () => {
    try {
      const res = await Http.get(`/label`);
      const labelsMap = {};
      if (res.data && res.data.data) {
        res.data.data.forEach((label) => {
          labelsMap[label.id] = {
            name: label.name,
            color: label.color,
          };
        });
      }
      setLabels(labelsMap);
    } catch (error) {
      console.error("Error fetching labels:", error);
    }
  };

  // Fetch developers
  const fetchDevelopers = async () => {
    try {
      const res = await Http.get(`/staff-department-wise/30`);
      const developersMap = {};
      if (Array.isArray(res.data)) {
        res.data.forEach((dev) => {
          const fullName = `${dev.first_name || ""} ${dev.last_name || ""}`.trim();
          developersMap[dev.id] = fullName || dev.name || `ID: ${dev.id}`;
        });
      }
      setDevelopers(developersMap);
    } catch (error) {
      console.error("Error fetching developers:", error);
      // Continue even if developers can't be fetched
    }
  };

  // Toggle story expansion
  const toggleStoryExpansion = (storyId) => {
    setExpandedStories((prev) => {
      const newState = {
        ...prev,
        [storyId]: !prev[storyId],
      };

      // Check if all stories are now expanded or collapsed
      const allStoriesExpanded = Object.values(newState).every((expanded) => expanded);

      // Only update allExpanded if individual tasks are also in the same state
      if (allStoriesExpanded === individualTasksExpanded) {
        setAllExpanded(allStoriesExpanded);
      } else if (!prev[storyId] === false) {
        // If this story is being collapsed, set allExpanded to false
        setAllExpanded(false);
      }

      return newState;
    });
  };

  // Filter stories and tasks based on search term and status
  const filteredStories = stories.filter(
    (story) =>
      (story.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (projects[story.project_id] && projects[story.project_id].toLowerCase().includes(searchTerm.toLowerCase()))) &&
      true
  );

  const filteredTasks = tasks.filter(
    (task) =>
      (task.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (task.project?.title && task.project.title.toLowerCase().includes(searchTerm.toLowerCase()))) &&
      true &&
      !task.story_id // Only include tasks that aren't part of a story in this filtered list
  );

  // Get tasks for a specific story
  const getTasksForStory = (storyId) => {
    return tasks.filter(
      (task) =>
        task.story_id === storyId &&
        (task.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
          (task.project?.title && task.project.title.toLowerCase().includes(searchTerm.toLowerCase()))) &&
        true
    );
  };

  // Calculate story status based on its tasks
  const calculateStoryStatus = (storyId) => {
    const storyTasks = tasks.filter((task) => task.story_id === storyId);

    if (storyTasks.length === 0) {
      return "To Do";
    }

    const allDone = storyTasks.every((task) => task.status === "Done");
    if (allDone) {
      return "Done";
    }

    const anyPaused = storyTasks.some((task) => task.status === "Pause");
    const anyStarted = storyTasks.some((task) => task.status === "In Progress" || task.status === "Review");

    if (anyStarted) {
      return "In Progress";
    }

    if (anyPaused) {
      // If there are paused tasks but no active ones, consider the story paused
      return "Pause";
    }

    return "To Do";
  };

  // Calculate story progress
  const calculateStoryProgress = (storyId) => {
    const storyTasks = tasks.filter((task) => task.story_id === storyId);

    if (storyTasks.length === 0) {
      return { percent: 0, inProgressCount: 0, doneCount: 0, totalCount: 0 };
    }

    const doneCount = storyTasks.filter((task) => task.status === "Done").length;
    const inProgressCount = storyTasks.filter((task) => task.status === "In Progress" || task.status === "Review").length;
    const totalCount = storyTasks.length;

    const percent = Math.round((doneCount / totalCount) * 100);

    return { percent, inProgressCount, doneCount, totalCount };
  };

  // Render progress bar for stories or individual tasks
  const renderProgressBar = (storyIdOrType) => {
    let tasksToRender = [];

    // Handle individual tasks case
    if (storyIdOrType === "individual-tasks") {
      tasksToRender = filteredTasks;
    } else {
      // Handle story tasks case
      tasksToRender = tasks.filter((task) => task.story_id === storyIdOrType);
    }

    if (tasksToRender.length === 0) {
      return (
        <div style={{ display: "flex", alignItems: "center", width: "100%" }}>
          <div style={{ fontSize: "11px", marginRight: "8px", color: "#6c757d" }}>No tasks</div>
          <div
            style={{
              width: "100%",
              height: "4px",
              backgroundColor: "#e9ecef",
              borderRadius: "2px",
              overflow: "hidden",
            }}
          ></div>
        </div>
      );
    }

    // Group tasks by status
    const statusGroups = {
      "To Do": tasksToRender.filter((task) => task.status === "To Do").length,
      "In Progress": tasksToRender.filter((task) => task.status === "In Progress").length,
      Pause: tasksToRender.filter((task) => task.status === "Pause").length,
      Review: tasksToRender.filter((task) => task.status === "Review").length,
      Done: tasksToRender.filter((task) => task.status === "Done").length,
    };

    const totalCount = tasksToRender.length;
    const doneCount = statusGroups["Done"];
    const percentDone = Math.round((doneCount / totalCount) * 100);

    // Calculate width for each section
    const widths = {
      Done: (statusGroups["Done"] / totalCount) * 100,
      Review: (statusGroups["Review"] / totalCount) * 100,
      "In Progress": (statusGroups["In Progress"] / totalCount) * 100,
      Pause: (statusGroups["Pause"] / totalCount) * 100,
      "To Do": (statusGroups["To Do"] / totalCount) * 100,
    };

    // Starting positions for each section
    const positions = {
      Done: 0,
      Review: widths["Done"],
      "In Progress": widths["Done"] + widths["Review"],
      Pause: widths["Done"] + widths["Review"] + widths["In Progress"],
      "To Do": widths["Done"] + widths["Review"] + widths["In Progress"] + widths["Pause"],
    };

    return (
      <div style={{ display: "flex", alignItems: "center", width: "100%" }}>
        <div style={{ fontSize: "11px", marginRight: "8px", color: "#6c757d", minWidth: "50px" }}>
          {doneCount}/{totalCount}
        </div>
        <div
          style={{
            width: "100%",
            height: "4px",
            backgroundColor: "#e9ecef",
            borderRadius: "2px",
            overflow: "hidden",
            position: "relative",
          }}
        >
          {widths["Done"] > 0 && (
            <div
              style={{
                position: "absolute",
                left: `${positions["Done"]}%`,
                top: 0,
                height: "100%",
                width: `${widths["Done"]}%`,
                backgroundColor: "#198754", // green for Done
                borderRadius: percentDone === 100 ? "2px" : "2px 0 0 2px",
              }}
            ></div>
          )}
          {widths["Review"] > 0 && (
            <div
              style={{
                position: "absolute",
                left: `${positions["Review"]}%`,
                top: 0,
                height: "100%",
                width: `${widths["Review"]}%`,
                backgroundColor: "#fd7e14", // orange for Review
              }}
            ></div>
          )}
          {widths["In Progress"] > 0 && (
            <div
              style={{
                position: "absolute",
                left: `${positions["In Progress"]}%`,
                top: 0,
                height: "100%",
                width: `${widths["In Progress"]}%`,
                backgroundColor: "#0d6efd", // blue for In Progress
              }}
            ></div>
          )}
          {widths["Pause"] > 0 && (
            <div
              style={{
                position: "absolute",
                left: `${positions["Pause"]}%`,
                top: 0,
                height: "100%",
                width: `${widths["Pause"]}%`,
                backgroundColor: "#dc3545", // red for Pause
              }}
            ></div>
          )}
        </div>
      </div>
    );
  };

  // Combine and sort stories by creation date (individual tasks shown separately)
  const combinedItems = loading
    ? []
    : [...filteredStories.map((story) => ({ ...story, itemType: "story", createdAt: new Date(story.created_at || story.updated_at || 0) }))].sort(
        (a, b) => b.createdAt - a.createdAt
      ); // Sort by creation date, newest first

  // Handle story deletion
  const confirmDeleteStory = (story) => {
    confirmDialog({
      message: `Are you sure you want to delete the story "${story.title}"? This will NOT delete any tasks under it.`,
      header: "Confirm Deletion",
      icon: "pi pi-exclamation-triangle",
      acceptClassName: "p-button-danger",
      accept: () => handleDeleteStory(story.id),
    });
  };

  // Fetch story data for editing
  const fetchStoryForEdit = async (storyId) => {
    try {
      const response = await Http.get(`/story/${storyId}`);
      if (response.data && response.data.data) {
        setStoryDataForEdit(response.data.data);
        setEditStoryVisible(true);
      } else {
        toast.current.show({
          severity: "error",
          summary: "Error",
          detail: "Failed to fetch story data for editing",
          life: 3000,
        });
      }
    } catch (error) {
      console.error("Error fetching story data:", error);
      toast.current.show({
        severity: "error",
        summary: "Error",
        detail: error.response?.data?.message || "Failed to fetch story data for editing",
        life: 3000,
      });
    }
  };

  // Fetch task data for editing
  const fetchTaskForEdit = async (taskId) => {
    try {
      const response = await Http.get(`/task/${taskId}`);
      if (response.data && response.data.data) {
        const taskData = response.data.data;

        // Ensure dates are properly formatted for input[type="date"]
        if (taskData.start_date) {
          // First ensure it's a valid date
          const startDate = new Date(taskData.start_date);
          if (!isNaN(startDate.getTime())) {
            taskData.start_date = startDate.toISOString().split("T")[0];
          } else {
            taskData.start_date = "";
          }
        }

        if (taskData.end_date) {
          // First ensure it's a valid date
          const endDate = new Date(taskData.end_date);
          if (!isNaN(endDate.getTime())) {
            taskData.end_date = endDate.toISOString().split("T")[0];
          } else {
            taskData.end_date = "";
          }
        }

        setTaskDataForEdit(taskData);
        setEditTaskVisible(true);
      } else {
        toast.current.show({
          severity: "error",
          summary: "Error",
          detail: "Failed to fetch task data for editing",
          life: 3000,
        });
      }
    } catch (error) {
      console.error("Error fetching task data:", error);
      toast.current.show({
        severity: "error",
        summary: "Error",
        detail: error.response?.data?.message || "Failed to fetch task data for editing",
        life: 3000,
      });
    }
  };

  const handleDeleteStory = async (storyId) => {
    try {
      await Http.delete(`/story/${storyId}`);
      fetchData();
      toast.current.show({
        severity: "success",
        summary: "Success",
        detail: "Story deleted successfully",
        life: 3000,
      });
    } catch (error) {
      console.error("Error deleting story:", error);
      toast.current.show({
        severity: "error",
        summary: "Error",
        detail: error.response?.data?.message || "Failed to delete story",
        life: 3000,
      });
    }
  };

  // Handle task deletion
  const confirmDeleteTask = (task) => {
    confirmDialog({
      message: `Are you sure you want to delete the task "${task.title}"?`,
      header: "Confirm Deletion",
      icon: "pi pi-exclamation-triangle",
      acceptClassName: "p-button-danger",
      accept: () => handleDeleteTask(task.id),
    });
  };

  const handleDeleteTask = async (taskId) => {
    try {
      await Http.delete(`/task/${taskId}`);
      fetchData();
      toast.current.show({
        severity: "success",
        summary: "Success",
        detail: "Task deleted successfully",
        life: 3000,
      });
    } catch (error) {
      console.error("Error deleting task:", error);
      toast.current.show({
        severity: "error",
        summary: "Error",
        detail: error.response?.data?.message || "Failed to delete task",
        life: 3000,
      });
    }
  };

  // Helper functions for rendering
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleDateString();
  };

  const getUserName = (user) => {
    if (!user) return "N/A";
    return user.name || `${user.first_name || ""} ${user.last_name || ""}`.trim() || `ID: ${user.id}`;
  };

  const renderDeveloperAvatar = (developerId) => {
    if (!developerId) return null;
    const name = developers[developerId] || "User";
    const initials = name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .substring(0, 2);

    return (
      <div
        className="avatar"
        style={{
          width: "28px",
          height: "28px",
          borderRadius: "50%",
          backgroundColor: "#0d6efd",
          color: "white",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: "12px",
          fontWeight: "bold",
        }}
      >
        {initials}
      </div>
    );
  };

  // Render status with appropriate color
  const renderStatus = (status) => {
    let color = "";
    switch (status) {
      case "To Do":
        color = "#6c757d"; // gray
        break;
      case "In Progress":
        color = "#0d6efd"; // blue
        break;
      case "Pause":
        color = "#dc3545"; // red
        break;
      case "Review":
        color = "#fd7e14"; // orange
        break;
      case "Done":
        color = "#198754"; // green
        break;
      default:
        color = "#6c757d"; // default gray
    }

    return (
      <span
        className="badge"
        style={{
          backgroundColor: color,
          color: "#fff",
          padding: "6px 10px",
          borderRadius: "4px",
          fontSize: "12px",
        }}
      >
        {status}
      </span>
    );
  };

  // Render label with color
  const renderLabel = (labelId) => {
    if (!labelId || !labels[labelId]) return null;
    const label = labels[labelId];

    return (
      <span
        className="badge"
        style={{
          // backgroundColor: label.color || "#ccc",
          backgroundColor: "transparent",
          color: label.color || "#ccc",
          border: `1px solid ${label.color || "#ccc"}`,
          padding: "3px 8px",
          borderRadius: "4px",
          fontSize: "12px",
          display: "inline-block",
          maxWidth: "100%",
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          fontWeight: "500",
          height: "24px",
          lineHeight: "18px",
          textAlign: "center",
          boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
        }}
      >
        {label.name}
      </span>
    );
  };

  // Get status color
  const getStatusColor = (status) => {
    switch (status) {
      case "To Do":
        return "#6c757d"; // gray
      case "In Progress":
        return "#0d6efd"; // blue
      case "Pause":
        return "#dc3545"; // red
      case "Review":
        return "#fd7e14"; // orange
      case "Done":
        return "#198754"; // green
      default:
        return "#6c757d"; // default gray
    }
  };

  // Handle status change
  const handleStatusChange = async (itemId, newStatus, itemType) => {
    try {
      // Check if task is being moved backward in workflow
      if (itemType === "task") {
        const task = tasks.find((t) => t.id === itemId);

        // Check if the user is assigned to the task
        if (!isUserAssignedToTask(task)) {
          toast.current.show({
            severity: "error",
            summary: "Permission Denied",
            detail: "You can only change status of tasks assigned to you",
            life: 3000,
          });
          return;
        }

        // Special handling for "Start" action on paused tasks
        if (task.status === "Pause" && newStatus === "Start") {
          // Get the task details with history to find previous status
          const taskResponse = await Http.get(`/task/${itemId}`);
          const taskHistory = taskResponse?.data?.data?.status_history || [];

          // Find the most recent pause entry to get the previous status
          const pauseEntry = taskHistory.find((entry) => entry.new_status === "Pause");
          let previousStatus = "In Progress"; // Default if no history found

          if (pauseEntry && pauseEntry.comment) {
            const match = pauseEntry.comment.match(/Task paused from (.*)/);
            if (match && match[1]) {
              previousStatus = match[1];
            }
          }

          // Update with the previous status
          const payload = {
            status: previousStatus,
            comment: `Task resumed to ${previousStatus} from Pause`,
          };

          await Http.patch(`/task/${itemId}/status`, payload);

          // Auto-pause other in-progress tasks for this user
          if (previousStatus === "In Progress" && currentUser) {
            // Find other tasks that are In Progress and assigned to the current user
            const otherInProgressTasks = tasks.filter(
              (t) => t.id !== itemId && (t.status === "In Progress" || t.status === "Review") && isUserAssignedToTask(t)
            );

            // If there are other tasks in progress, pause them automatically
            if (otherInProgressTasks.length > 0) {
              // Show toast notifying of auto-pause
              toast.current.show({
                severity: "info",
                summary: "Auto-Pause",
                detail: `Automatically pausing ${otherInProgressTasks.length} other active task(s)`,
                life: 3000,
              });

              // Pause each task one by one
              for (const taskToPause of otherInProgressTasks) {
                const pausePayload = {
                  status: "Pause",
                  comment: `Task auto-paused from ${taskToPause.status} when starting task #${itemId}`,
                  previous_status: taskToPause.status,
                };

                await Http.patch(`/task/${taskToPause.id}/status`, pausePayload);

                // Update local state for paused task
                setTasks((prevTasks) => prevTasks.map((t) => (t.id === taskToPause.id ? { ...t, status: "Pause" } : t)));
              }
            }
          }

          // Update local state for the resumed task
          setTasks((prevTasks) => prevTasks.map((t) => (t.id === itemId ? { ...t, status: previousStatus } : t)));

          // Highlight the updated task
          highlightItem(itemId, "task");

          toast.current.show({
            severity: "success",
            summary: "Success",
            detail: `Task resumed to ${previousStatus} status`,
            life: 3000,
          });

          return;
        }

        // Check if status transition is valid for non-paused tasks
        if (task && !isValidStatusTransition(task.status, newStatus)) {
          toast.current.show({
            severity: "error",
            summary: "Invalid Transition",
            detail: newStatus === "Pause" ? "Tasks can only be paused if they are in progress or in review" : "Invalid status transition",
            life: 3000,
          });
          return;
        }

        // If transitioning to In Progress, auto-pause other In Progress tasks for this user
        if (newStatus === "In Progress" && currentUser) {
          // Find other tasks that are In Progress and assigned to the current user
          const otherInProgressTasks = tasks.filter(
            (t) => t.id !== itemId && (t.status === "In Progress" || t.status === "Review") && isUserAssignedToTask(t)
          );

          // If there are other tasks in progress, pause them automatically
          if (otherInProgressTasks.length > 0) {
            // Show toast notifying of auto-pause
            toast.current.show({
              severity: "info",
              summary: "Auto-Pause",
              detail: `Automatically pausing ${otherInProgressTasks.length} other active task(s)`,
              life: 3000,
            });

            // Pause each task one by one
            for (const taskToPause of otherInProgressTasks) {
              const pausePayload = {
                status: "Pause",
                comment: `Task auto-paused from ${taskToPause.status} when starting task #${itemId}`,
                previous_status: taskToPause.status,
              };

              await Http.patch(`/task/${taskToPause.id}/status`, pausePayload);

              // Update local state for paused task
              setTasks((prevTasks) => prevTasks.map((t) => (t.id === taskToPause.id ? { ...t, status: "Pause" } : t)));
            }
          }
        }

        // Prepare additional data for status transition
        let endpoint = `/task/${itemId}/status`;
        let payload = {
          status: newStatus,
          comment: "",
        };

        // If transitioning to Pause, store the current status for later return
        if (newStatus === "Pause") {
          payload.comment = `Task paused from ${task.status}`;
          payload.previous_status = task.status;
        }

        await Http.patch(endpoint, payload);
      } else {
        // Handle story status changes (unchanged)
        const endpoint = `/story/${itemId}`;
        const payload = { status: newStatus };
        await Http.patch(endpoint, payload);
      }

      // Update local state instead of fetching all data again
      if (itemType === "story") {
        // For stories, update the story state
        setStories((prevStories) => prevStories.map((story) => (story.id === itemId ? { ...story, status: newStatus } : story)));
        // Highlight the updated story
        highlightItem(itemId, "story");
      } else {
        // For tasks, update the task state
        setTasks((prevTasks) => prevTasks.map((task) => (task.id === itemId ? { ...task, status: newStatus } : task)));
        // Highlight the updated task
        highlightItem(itemId, "task");
      }

      toast.current.show({
        severity: "success",
        summary: "Success",
        detail: `${itemType === "story" ? "Story" : "Task"} status updated successfully`,
        life: 3000,
      });
    } catch (error) {
      console.error(`Error updating ${itemType} status:`, error);
      toast.current.show({
        severity: "error",
        summary: "Error",
        detail: error.response?.data?.message || `Failed to update ${itemType} status`,
        life: 3000,
      });
    }
  };

  // Render all developer avatars and names
  const renderDevelopers = (developers) => {
    if (!developers || developers.length === 0) return null;

    // Limit displayed developers to 3 with a +N indicator for additional ones
    const displayLimit = 3;
    const displayedDevelopers = developers.slice(0, displayLimit);
    const remainingCount = developers.length - displayLimit;

    return (
      <div className="d-flex align-items-center justify-content-center">
        {displayedDevelopers.map((dev, index) => (
          <div
            key={dev.id}
            className="developer-badge"
            style={{
              marginRight: "2px",
              position: "relative",
            }}
          >
            {renderDeveloperAvatar(dev.id)}
          </div>
        ))}

        {/* Show indicator for additional developers */}
        {remainingCount > 0 && (
          <div
            style={{
              width: "28px",
              height: "28px",
              borderRadius: "50%",
              backgroundColor: "#6c757d",
              color: "white",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: "11px",
              fontWeight: "bold",
              marginLeft: "2px",
            }}
          >
            +{remainingCount}
          </div>
        )}
      </div>
    );
  };

  // Toggle individual tasks expansion
  const toggleIndividualTasksExpansion = () => {
    const newState = !individualTasksExpanded;
    setIndividualTasksExpanded(newState);

    // Check if all stories are expanded
    const allStoriesExpanded = Object.values(expandedStories).every((expanded) => expanded);

    // Update allExpanded based on both individual tasks and stories
    if (newState === allStoriesExpanded) {
      setAllExpanded(newState);
    } else if (newState === false) {
      // If individual tasks are being collapsed, set allExpanded to false
      setAllExpanded(false);
    }
  };

  // Get status dropdown options - special handling for Paused tasks
  const getStatusDropdownOptions = (taskStatus) => {
    if (taskStatus === "Pause") {
      // For paused tasks, just show Start option
      return [{ label: "Start", value: "Start" }];
    }
    // For other statuses, show normal options
    return statusOptions;
  };

  // Render backlog item (can be story or task)
  const renderBacklogItem = (item, itemType, indentLevel = 0) => {
    const isStory = itemType === "story";
    const hasSubtasks = isStory && getTasksForStory(item.id).length > 0;
    const isDescriptionExpanded = expandedDescriptions[item.id] || false;
    const isHighlighted = highlightedItems[`${itemType}-${item.id}`] || false;
    const canModifyTask = !isStory && isUserAssignedToTask(item);

    // For stories, calculate status based on tasks
    const storyStatus = isStory ? calculateStoryStatus(item.id) : null;

    // Get task type data if this is a task
    const taskType = !isStory && item.task_type_id && taskTypes[item.task_type_id] ? taskTypes[item.task_type_id] : null;

    // Current status and options
    const currentStatus = item.status || "To Do";
    const statusDropdownOptions = !isStory ? getStatusDropdownOptions(currentStatus) : [];

    return (
      <React.Fragment key={`${itemType}-${item.id}`}>
        <div
          className="backlog-item shadow"
          style={{
            padding: isStory ? "10px 16px" : "12px 16px",
            borderBottom: "1px solid #e9ecef",
            display: "flex",
            alignItems: "center",
            backgroundColor: isHighlighted ? "#fff8e1" : isStory ? "rgb(242 243 244)" : "white",
            boxShadow: isStory ? "0 1px 2px rgba(0,0,0,0.05)" : "none",
            marginLeft: `${indentLevel * 40}px`,
            cursor: isStory ? "pointer" : "default",
            position: "relative",
            borderLeft: isStory ? "3px solid #0747A6" : "none",
            borderRadius: "5px",
            transition: "background-color 0.3s ease",
            opacity: isStory || canModifyTask ? 1 : 0.85,
            ...(isHighlighted && {
              animation: "pulse-highlight 2s ease",
            }),
          }}
          onClick={() => isStory && toggleStoryExpansion(item.id)}
        >
          {!isStory && !canModifyTask && (
            <div
              style={{
                position: "absolute",
                top: "2px",
                right: "2px",
                fontSize: "10px",
                color: "#6c757d",
              }}
            >
              <i className="pi pi-lock" title="You cannot modify this task's status"></i>
            </div>
          )}

          {/* Toggle arrow and Type Indicator Column - Fixed width */}
          <div style={{ display: "flex", width: "60px", flexShrink: 0 }}>
            {/* Toggle arrow for stories */}
            {isStory && (
              <div style={{ width: "20px", marginRight: "8px" }}>
                {hasSubtasks ? (
                  <i className={`pi ${expandedStories[item.id] ? "pi-chevron-down" : "pi-chevron-right"}`} style={{ color: "#666" }} />
                ) : (
                  <i className="pi pi-minus" style={{ color: "#ccc" }} />
                )}
              </div>
            )}

            {/* Item Type Indicator */}
            <div style={{ width: "20px", textAlign: "center" }}>
              {isStory ? (
                <i
                  className="pi pi-bookmark"
                  style={{
                    color: hasSubtasks ? "#0747A6" : "#97A0AF",
                    fontSize: "16px",
                  }}
                ></i>
              ) : (
                <i
                  className={taskType?.icon || "pi pi-check-square"}
                  style={{
                    color: taskType?.color || "#4CAF50",
                    fontSize: "16px",
                  }}
                ></i>
              )}
            </div>
          </div>

          {/* Item Title and Details Column - Flexible width */}
          <div style={{ flex: 1, minWidth: 0, paddingRight: "16px" }}>
            <div
              style={{
                fontWeight: isStory ? "600" : "500",
                fontSize: isStory ? "14px" : "13px",
                marginBottom: isStory ? "2px" : "3px",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {item.title}
              {!isStory && taskType && (
                <span
                  style={{
                    fontSize: "11px",
                    marginLeft: "8px",
                    color: taskType.color,
                    border: `1px solid ${taskType.color}`,
                    borderRadius: "3px",
                    padding: "1px 4px",
                  }}
                >
                  {taskType.name}
                </span>
              )}
            </div>
            <div style={{ display: "flex", alignItems: "center", fontSize: "11px", color: "#6c757d" }}>
              {isStory ? (
                <>
                  <span style={{ marginRight: "12px" }}>
                    <i className="pi pi-folder" style={{ marginRight: "4px" }}></i>
                    {projects[item.project_id] || "N/A"}
                  </span>
                  {item.story_points && (
                    <span style={{ marginRight: "12px" }}>
                      <i className="pi pi-chart-bar" style={{ marginRight: "4px" }}></i>
                      {item.story_points} points
                    </span>
                  )}
                </>
              ) : (
                <>
                  <span style={{ marginRight: "12px" }}>
                    <i className="pi pi-calendar" style={{ marginRight: "4px" }}></i>
                    {formatDate(item.start_date)} - {formatDate(item.end_date)}
                  </span>
                </>
              )}

              {/* Right side - Progress bar for stories, extra details for tasks */}
              {isStory ? (
                <div style={{ flex: 1, paddingRight: "16px" }}>
                  {hasSubtasks ? renderProgressBar(item.id) : <div style={{ fontSize: "11px", color: "#6c757d" }}>No tasks</div>}
                </div>
              ) : (
                <div style={{ display: "flex", alignItems: "center", marginTop: "2px", fontSize: "11px", color: "#6c757d" }}>
                  {item.description && (
                    <span
                      style={{
                        cursor: "pointer",
                        color: isDescriptionExpanded ? "#0747A6" : "#6c757d",
                        textDecoration: "underline",
                        marginRight: "12px",
                      }}
                      onClick={(e) => toggleDescriptionExpansion(item.id, e)}
                    >
                      <i className={`pi ${isDescriptionExpanded ? "pi-chevron-up" : "pi-chevron-down"}`} style={{ marginRight: "4px" }}></i>
                      {isDescriptionExpanded ? "Hide details" : "Show details"}
                    </span>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Status Column - Fixed width */}
          <div
            style={{
              width: "120px",
              flexShrink: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {!isStory ? (
              /* Dropdown for tasks */
              <div className="status-dropdown-container" onClick={(e) => e.stopPropagation()}>
                {item.status === "Pause" ? (
                  /* Custom Start button for paused tasks */
                  <button
                    onClick={() => {
                      if (canModifyTask) {
                        handleStatusChange(item.id, "Start", itemType);
                      } else {
                        toast.current.show({
                          severity: "error",
                          summary: "Permission Denied",
                          detail: "You can only change status of tasks assigned to you",
                          life: 3000,
                        });
                      }
                    }}
                    className="p-inputtext-sm"
                    disabled={!canModifyTask}
                    style={{
                      height: "24px",
                      fontSize: "12px",
                      fontWeight: 600,
                      backgroundColor: "#dc3545", // Red for Pause
                      border: "none",
                      color: "#fff",
                      width: "100px",
                      padding: "1px 8px",
                      borderRadius: "4px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      cursor: canModifyTask ? "pointer" : "default",
                      opacity: canModifyTask ? 1 : 0.7,
                    }}
                  >
                    Start
                  </button>
                ) : (
                  /* Regular dropdown for other statuses */
                  <Dropdown
                    value={item.status}
                    options={statusDropdownOptions}
                    onChange={(e) => {
                      // Before calling handleStatusChange, check if the user can modify
                      if (!canModifyTask) {
                        toast.current.show({
                          severity: "error",
                          summary: "Permission Denied",
                          detail: "You can only change status of tasks assigned to you",
                          life: 3000,
                        });
                        return;
                      }

                      // For regular status changes, check transition validity
                      if (isValidStatusTransition(item.status, e.value) || e.value === item.status) {
                        handleStatusChange(item.id, e.value, itemType);
                      } else {
                        toast.current.show({
                          severity: "error",
                          summary: "Invalid Transition",
                          detail: "Tasks can only be moved forward in the workflow",
                          life: 3000,
                        });
                      }
                    }}
                    className="p-inputtext-sm"
                    disabled={!canModifyTask}
                    style={{
                      height: "24px",
                      fontSize: "12px",
                      backgroundColor: getStatusColor(item.status),
                      border: "none",
                      color: "#fff",
                      width: "100px",
                      padding: "1px 8px",
                      borderRadius: "4px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      opacity: canModifyTask ? 1 : 0.7,
                    }}
                    itemTemplate={(option) => (
                      <div
                        style={{
                          padding: "3px 8px",
                          fontSize: "12px",
                          backgroundColor: !option ? "#6c757d" : option.value === "Start" ? "#28a745" : getStatusColor(option.value),
                          color: "#fff",
                          borderRadius: "3px",
                          margin: "2px 0",
                          textAlign: "center",
                          width: "100%",
                        }}
                      >
                        {option?.label || ""}
                      </div>
                    )}
                    valueTemplate={(option) => (
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          width: "100%",
                        }}
                      >
                        <span style={{ flex: 1, textAlign: "center" }}>{option?.label || item.status}</span>
                        <i className="pi pi-chevron-down" style={{ fontSize: "9px", marginLeft: "4px", opacity: 0.8 }}></i>
                      </div>
                    )}
                    panelStyle={{ width: "120px", padding: "4px" }}
                  />
                )}
              </div>
            ) : (
              // Empty placeholder for stories to maintain alignment
              <div style={{ width: "100px" }}></div>
            )}
          </div>

          {/* Label Column - Fixed width */}
          <div
            style={{
              width: "120px",
              flexShrink: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {item.label_id ? renderLabel(item.label_id) : <div style={{ width: "75px" }}></div>}
          </div>

          {/* Developers Column - Fixed width */}
          <div
            style={{
              width: "100px",
              flexShrink: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {isStory && item.developer_id ? (
              renderDeveloperAvatar(item.developer_id)
            ) : !isStory && item.developers && item.developers.length > 0 ? (
              renderDevelopers(item.developers)
            ) : (
              <div style={{ width: "28px" }}></div>
            )}
          </div>

          {/* Actions Column - Fixed width */}
          <div
            style={{
              width: "100px",
              flexShrink: 0,
              display: "flex",
              gap: "4px",
              justifyContent: "flex-end",
            }}
          >
            {isStory && (
              <Button
                icon="pi pi-plus"
                className="p-button-rounded p-button-outlined p-button-sm"
                style={{ width: "24px", height: "24px" }}
                onClick={(e) => {
                  e.stopPropagation();
                  setSelectedStory(item);
                  setCreateTaskVisible(true);
                }}
                tooltip="Add Task"
                tooltipOptions={{ position: "top" }}
              />
            )}
            <Button
              icon="pi pi-pencil"
              className="p-button-rounded p-button-outlined p-button-sm"
              style={{ width: "24px", height: "24px" }}
              onClick={(e) => {
                e.stopPropagation();
                if (isStory) {
                  setSelectedStory(item);
                  fetchStoryForEdit(item.id);
                } else {
                  // Check if user is assigned to this task before allowing edit
                  if (!canModifyTask) {
                    toast.current.show({
                      severity: "error",
                      summary: "Permission Denied",
                      detail: "You can only edit tasks assigned to you",
                      life: 3000,
                    });
                    return;
                  }
                  setSelectedTask(item);
                  fetchTaskForEdit(item.id);
                }
              }}
              tooltip="Edit"
              tooltipOptions={{ position: "top" }}
              disabled={!isStory && !canModifyTask}
            />
            {/* {!isStory && (
              <Button
                icon="pi pi-sync"
                className="p-button-rounded p-button-outlined p-button-sm"
                style={{ width: "24px", height: "24px" }}
                onClick={(e) => {
                  e.stopPropagation();
                  setSelectedTask(item);
                  setStatusUpdateVisible(true);
                }}
                tooltip="Update Status"
                tooltipOptions={{ position: "top" }}
                disabled={!canModifyTask}
              />
            )} */}
            <Button
              icon="pi pi-trash"
              className="p-button-rounded p-button-outlined p-button-danger p-button-sm"
              style={{ width: "24px", height: "24px" }}
              onClick={(e) => {
                e.stopPropagation();
                if (isStory) {
                  confirmDeleteStory(item);
                } else {
                  confirmDeleteTask(item);
                }
              }}
              tooltip="Delete"
              tooltipOptions={{ position: "top" }}
            />
          </div>
        </div>

        {/* Render tasks for this story if expanded */}
        {isStory && expandedStories[item.id] && getTasksForStory(item.id).map((task) => renderBacklogItem(task, "task", 1))}

        {/* Show task description when expanded */}
        {!isStory && isDescriptionExpanded && item.description && (
          <div
            className="task-description"
            style={{
              padding: "12px 16px 12px 76px",
              backgroundColor: "#f8f9fa",
              borderBottom: "1px solid #e9ecef",
              marginLeft: `${indentLevel * 40}px`,
              fontSize: "13px",
              position: "relative",
              lineHeight: "1.5",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ maxWidth: "100%", overflowX: "auto" }}>
              <div style={{ marginBottom: "8px", fontWeight: "500" }}>Description:</div>
              <div dangerouslySetInnerHTML={{ __html: item.description }} />
            </div>
          </div>
        )}
      </React.Fragment>
    );
  };

  // Calculate progress specifically for individual tasks
  const renderIndividualTasksHeader = () => {
    // Calculate progress for individual tasks
    const individualTasksCount = filteredTasks.length;
    const doneCount = filteredTasks.filter((task) => task.status === "Done").length;
    const percentDone = individualTasksCount > 0 ? Math.round((doneCount / individualTasksCount) * 100) : 0;

    // Determine if there are any in-progress tasks
    const hasInProgressTasks = filteredTasks.some((task) => task.status === "In Progress" || task.status === "Review");

    // Determine status based on tasks
    let status = "To Do";
    if (individualTasksCount === 0) {
      status = "To Do";
    } else if (percentDone === 100) {
      status = "Done";
    } else if (hasInProgressTasks) {
      status = "In Progress";
    }

    return (
      <div
        className="backlog-item shadow"
        style={{
          padding: "10px 16px",
          borderBottom: "1px solid #e9ecef",
          display: "flex",
          alignItems: "center",
          backgroundColor: "rgb(242 243 244)",
          boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
          cursor: "pointer",
          position: "relative",
          borderLeft: "3px solid #0747A6",
          borderRadius: "5px",
          transition: "background-color 0.3s ease",
        }}
        onClick={toggleIndividualTasksExpansion}
      >
        {/* Toggle arrow column - Fixed width */}
        <div style={{ display: "flex", width: "60px", flexShrink: 0 }}>
          <div style={{ width: "20px", marginRight: "8px" }}>
            {filteredTasks.length > 0 ? (
              <i className={`pi ${individualTasksExpanded ? "pi-chevron-down" : "pi-chevron-right"}`} style={{ color: "#666" }} />
            ) : (
              <i className="pi pi-minus" style={{ color: "#ccc" }} />
            )}
          </div>
          <div style={{ width: "20px", textAlign: "center" }}>
            <i
              className="pi pi-list"
              style={{
                color: filteredTasks.length > 0 ? "#0747A6" : "#97A0AF",
                fontSize: "16px",
              }}
            ></i>
          </div>
        </div>

        {/* Title and Details Column - Flexible width */}
        <div style={{ flex: 1, minWidth: 0, paddingRight: "16px" }}>
          <div
            style={{
              fontWeight: "600",
              fontSize: "14px",
              marginBottom: "2px",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            Individual Tasks
          </div>
          <div style={{ display: "flex", alignItems: "center", fontSize: "11px", color: "#6c757d" }}>
            <span style={{ marginRight: "12px" }}>
              <i className="pi pi-check-square" style={{ marginRight: "4px" }}></i>
              {filteredTasks.length} tasks
            </span>

            {/* Progress bar */}
            <div style={{ flex: 1, paddingRight: "16px" }}>
              {filteredTasks.length > 0 ? renderProgressBar("individual-tasks") : <div style={{ fontSize: "11px", color: "#6c757d" }}>No tasks</div>}
            </div>
          </div>
        </div>

        {/* Status Column - Fixed width */}
        <div
          style={{
            width: "120px",
            flexShrink: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <div style={{ width: "100px" }}></div>
        </div>

        {/* Label Column - Fixed width */}
        <div
          style={{
            width: "120px",
            flexShrink: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <div style={{ width: "75px" }}></div>
        </div>

        {/* Developers Column - Fixed width */}
        <div
          style={{
            width: "100px",
            flexShrink: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <div style={{ width: "28px" }}></div>
        </div>

        {/* Actions Column - Fixed width */}
        <div
          style={{
            width: "100px",
            flexShrink: 0,
            display: "flex",
            gap: "4px",
            justifyContent: "flex-end",
          }}
        >
          <Button
            icon="pi pi-plus"
            className="p-button-rounded p-button-outlined p-button-sm"
            style={{ width: "24px", height: "24px" }}
            onClick={(e) => {
              e.stopPropagation();
              setCreateTaskVisible(true);
            }}
            tooltip="Add Task"
            tooltipOptions={{ position: "top" }}
          />
        </div>
      </div>
    );
  };

  // Main render function
  return (
    <div>
      <IsLogin />
      <Toast ref={toast} />
      <ConfirmDialog />
      <style>{statusDropdownStyle}</style>

      <div className="am-mainpanel">
        <div className="am-pagetitle">
          <h5 className="am-title">Backlog</h5>
          <div className="d-flex justify-content-between align-items-center w-100 mt-2">
            <div className="d-flex align-items-center">
              <span className="p-input-icon-left mr-3" style={{ width: "300px" }}>
                <i className="pi pi-search" style={{ marginLeft: "4px" }} />
                <InputText
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search backlog..."
                  className="w-100"
                  style={{ paddingLeft: "20px" }}
                />
              </span>
              <Button
                label={allExpanded ? "Collapse All" : "Expand All"}
                icon={allExpanded ? "pi pi-minus" : "pi pi-plus"}
                className="p-button-outlined ms-2"
                onClick={toggleAllExpanded}
              />
            </div>
            <div>
              <Menu model={createMenuItems} popup ref={createMenuRef} />
              <Button
                label="Create"
                icon="pi pi-plus"
                className="p-button-success"
                onClick={(e) => createMenuRef.current.toggle(e)}
                aria-controls="create-menu"
                aria-haspopup
              />
            </div>
          </div>
        </div>

        <div className="am-pagebody">
          <div className="card pd-20 pd-sm-40">
            {error && (
              <div className="alert alert-danger" role="alert">
                {error}
              </div>
            )}

            {loading ? (
              <div className="text-center p-5">
                <i className="pi pi-spin pi-spinner" style={{ fontSize: "2rem" }}></i>
                <p className="mt-2">Loading backlog...</p>
              </div>
            ) : combinedItems.length === 0 && filteredTasks.length === 0 ? (
              <div className="text-center p-5">
                <i className="pi pi-folder-open" style={{ fontSize: "3rem", color: "#ccc" }}></i>
                <p className="mt-2">No items found. Create your first story or task!</p>
              </div>
            ) : (
              <div className="backlog-container" style={{ backgroundColor: "#f8f9fa", borderRadius: "6px" }}>
                {/* Column headers */}
                <div
                  style={{
                    display: "flex",
                    padding: "8px 16px",
                    backgroundColor: "#e9ecef",
                    borderBottom: "1px solid #ced4da",
                    fontWeight: "600",
                    fontSize: "13px",
                    color: "#495057",
                    position: "sticky",
                    top: 0,
                    zIndex: 5,
                  }}
                >
                  {/* Toggle and Title column */}
                  <div style={{ display: "flex", flex: 1, minWidth: 0 }}>
                    <div style={{ width: "60px" }}></div>
                    <div>Title</div>
                  </div>

                  {/* Status column */}
                  <div style={{ width: "120px", textAlign: "center" }}>Status</div>

                  {/* Label column */}
                  <div style={{ width: "120px", textAlign: "center" }}>Label</div>

                  {/* Developers column */}
                  <div style={{ width: "100px", textAlign: "center" }}>Developers</div>

                  {/* Actions column */}
                  <div style={{ width: "100px", textAlign: "right" }}>Actions</div>
                </div>

                {/* Render stories sorted by creation date */}
                {combinedItems.map((item) => renderBacklogItem(item, item.itemType))}

                {/* Render Individual Tasks section if there are any standalone tasks */}
                {filteredTasks.length > 0 && (
                  <>
                    {renderIndividualTasksHeader()}
                    {individualTasksExpanded && filteredTasks.map((task) => renderBacklogItem(task, "task", 1))}
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Dialogs */}
      <Dialog
        header="Create Story"
        visible={createStoryVisible}
        style={{ width: "80%", maxWidth: "900px" }}
        onHide={() => setCreateStoryVisible(false)}
      >
        <CreateStory
          setVisible={setCreateStoryVisible}
          onSuccess={(newStoryData) => {
            // Add new story directly to the state instead of refreshing all data
            if (newStoryData) {
              handleAddNewStory(newStoryData);
            }
            setCreateStoryVisible(false);
            toast.current.show({
              severity: "success",
              summary: "Success",
              detail: "Story created successfully",
              life: 3000,
            });
          }}
        />
      </Dialog>

      <Dialog
        header="Edit Story"
        visible={editStoryVisible}
        style={{ width: "80%", maxWidth: "900px" }}
        onHide={() => {
          setEditStoryVisible(false);
          setStoryDataForEdit(null);
        }}
      >
        {storyDataForEdit && (
          <EditStory
            rowData={storyDataForEdit}
            setVisible={setEditStoryVisible}
            onSuccess={(updatedStoryData) => {
              // Update story directly in the state without refreshing all data
              if (updatedStoryData) {
                handleUpdateStory(updatedStoryData);
              }
              setEditStoryVisible(false);
              setStoryDataForEdit(null);
              toast.current.show({
                severity: "success",
                summary: "Success",
                detail: "Story updated successfully",
                life: 3000,
              });
            }}
          />
        )}
      </Dialog>

      <Dialog
        header="Create Task"
        visible={createTaskVisible}
        style={{ width: "80%", maxWidth: "900px" }}
        onHide={() => {
          setCreateTaskVisible(false);
          setSelectedStory(null);
        }}
      >
        <CreateTask
          initialStoryId={selectedStory?.id}
          onSuccess={(newTaskData) => {
            // Add new task directly to the state instead of refreshing all data
            if (newTaskData) {
              handleAddNewTask(newTaskData);
            }
            setCreateTaskVisible(false);
            toast.current.show({
              severity: "success",
              summary: "Success",
              detail: "Task created successfully",
              life: 3000,
            });
          }}
        />
      </Dialog>

      <Dialog
        header="Edit Task"
        visible={editTaskVisible}
        style={{ width: "80%", maxWidth: "900px" }}
        onHide={() => {
          setEditTaskVisible(false);
          setTaskDataForEdit(null);
        }}
      >
        {taskDataForEdit && (
          <>
            {!isUserAssignedToTask(taskDataForEdit) ? (
              <div className="alert alert-warning">
                <i className="pi pi-exclamation-triangle mr-2"></i>
                You cannot edit this task because you are not assigned to it.
              </div>
            ) : (
              <EditTask
                taskId={taskDataForEdit.id}
                setVisible={setEditTaskVisible}
                onSuccess={(updatedTaskData) => {
                  // Update task directly in the state without refreshing all data
                  if (updatedTaskData) {
                    handleUpdateTask(updatedTaskData);
                  }
                  setEditTaskVisible(false);
                  setTaskDataForEdit(null);
                  toast.current.show({
                    severity: "success",
                    summary: "Success",
                    detail: "Task updated successfully",
                    life: 3000,
                  });
                }}
              />
            )}
          </>
        )}
      </Dialog>

      <Dialog header="Update Task Status" visible={statusUpdateVisible} style={{ width: "500px" }} onHide={() => setStatusUpdateVisible(false)}>
        {selectedTask && (
          <>
            {!isUserAssignedToTask(selectedTask) ? (
              <div className="alert alert-warning">
                <i className="pi pi-exclamation-triangle mr-2"></i>
                You cannot change the status of this task because you are not assigned to it.
              </div>
            ) : (
              <StatusUpdate
                taskId={selectedTask.id}
                currentStatus={selectedTask.status}
                validStatusTransitions={statusOptions
                  .filter((option) => option.value === selectedTask.status || isValidStatusTransition(selectedTask.status, option.value))
                  .map((option) => option.value)}
                onSuccess={(updatedTaskData) => {
                  // Update task directly in the state without refreshing all data
                  if (updatedTaskData) {
                    handleUpdateTask(updatedTaskData);
                  }
                  setStatusUpdateVisible(false);
                  toast.current.show({
                    severity: "success",
                    summary: "Success",
                    detail: "Task status updated successfully",
                    life: 3000,
                  });
                }}
                setVisible={setStatusUpdateVisible}
              />
            )}
          </>
        )}
      </Dialog>

      {/* Add animation styles */}
      <style>
        {`
          @keyframes pulse-highlight {
            0% { background-color: #fff8e1; }
            50% { background-color: #ffecb3; }
            100% { background-color: #fff8e1; }
          }
        `}
      </style>
    </div>
  );
};

export default Backlog;
